import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { SkeletonRows } from '@components/table';
import { fonts } from '@theme/fontsCustomer';
import { ErrorIcon } from '@icons/ErrorIcon';
import type { WorkflowModel } from '@repositories/workflowRepository/models/WorkflowModel';
import { WorkflowRow } from './WorkflowRow';

type Props = {
  workflows: WorkflowModel[];
  isLoading: boolean;
};
export const WorkflowListTable = (props: Props) => {
  const searchName = new URLSearchParams(window.location.search).get('name');
  const { workflows, isLoading } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <TableWrapper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell width="72px" align="center">
                {t('Workflow_Main_04')}
              </StyledTableCell>
              <TableCell width="556px">{t('Workflow_Main_05')}</TableCell>
              <TableCell width="240px">{t('Workflow_Main_06')}</TableCell>
              <TableCell width="80px">{t('Workflow_Main_07')}</TableCell>
              <TableCell width="180px">{t('Workflow_Main_08')}</TableCell>
              <TableCell width="200px">{t('Workflow_Main_09')}</TableCell>
              <StyledTableCell width="72px" align="center">
                {t('Acc_Main_23')}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows rowNum={3} colNum={7} />
            ) : workflows.length ? (
              workflows.map((workflow, idx) => {
                return <WorkflowRow data={workflow} key={workflow.id} idx={idx} />;
              })
            ) : (
              <NoDataRow>
                <TableCell colSpan={7} rowSpan={5}>
                  <Empty>
                    <Avatar
                      sx={{
                        backgroundColor: theme.colors['bg-primary-lighter'],
                      }}
                    >
                      <ErrorIcon color={theme.colors['ic-primary-light']} />
                    </Avatar>
                    {searchName ? (
                      <div className="main-text">{t('Workflow_Main_22')}</div>
                    ) : (
                      <>
                        <div className="main-text">{t('Workflow_Main_20')}</div>
                        <div className="sub-text">{t('Workflow_Main_21')}</div>
                      </>
                    )}
                  </Empty>
                </TableCell>
              </NoDataRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableWrapper>
  );
};

const TableWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 29px;
  table {
    display: table;
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
    border-collapse: collapse;
    border-radius: 5px;
    border-style: hidden;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors['border-gray-lighter']};
    overflow: hidden;
  }
  & .MuiTableRow-root {
    cursor: pointer;
  }
  th {
    text-align: left;
    color: ${({ theme }) => theme.colors['text-gray-main']};
    border: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
    background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  }
  td {
    color: ${({ theme }) => theme.colors['text-gray-main']};
    border: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
  }
`;

const NoDataRow = styled(TableRow)`
  &.MuiTableRow-root:hover {
    background-color: ${({ theme }) => theme.colors['bg-white']};
  }
`;

const Empty = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 28px 0px 30px 0px;
  align-items: center;
  & .main-text {
    ${fonts.Body2};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    margin-top: 12px;
    white-space: pre-line;
    text-align: center;
  }
  & .sub-text {
    ${fonts.Body2};
    color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
  }
`;

const StyledTableCell = styled(TableCell)`
  &.MuiTableCell-root {
    text-align: center;
  }
`;
